/**
 * @fileoverview Which Active Clients dialog is open, and what each outcome says.
 * @description Mounts the details modal or one of the four action dialogs (Invite HR Admin, Edit,
 * Archive, Restore) for the client the page points at, and turns each outcome into the page's flash
 * message. An action dialog opens over the client's details and returns to them when it is
 * cancelled; a saved change closes everything and the sales queries refresh the grid, the KPI tiles
 * and the details. A client that no longer exists closes everything, reloads the grid and says so.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { useRefreshSalesQueries } from '@/features/sales/hooks/useRefreshSalesQueries'

import ClientArchiveDialog from './ClientArchiveDialog'
import ClientDetailModal from './ClientDetailModal'
import EditClientDialog from './EditClientDialog'
import InviteHrAdminDialog from './InviteHrAdminDialog'

import type { ClientArchiveMode } from './ClientArchiveDialog'
import type { ClientDialogKind } from './ClientDetailModal'

/** @description The client the page points at and which of its dialogs is open. */
export interface ClientDialogTarget {
  tenantId: string
  bankName: string
  view: 'details' | ClientDialogKind
}

interface ActiveClientsDialogsProps {
  target: ClientDialogTarget | null
  onTarget: (target: ClientDialogTarget | null) => void
  onFlash: (message: string) => void
}

/**
 * @component
 * @param {ActiveClientsDialogsProps} props - The open dialog and the page's handlers.
 * @param {ClientDialogTarget | null} props.target - The client and dialog now open, or null.
 * @param {(target: ClientDialogTarget | null) => void} props.onTarget - Opens or closes a dialog.
 * @param {(message: string) => void} props.onFlash - Shows the page's confirmation message.
 */
function ActiveClientsDialogs({ target, onTarget, onFlash }: ActiveClientsDialogsProps) {
  const { t } = useTranslation()
  const refreshSales = useRefreshSalesQueries()

  if (!target) return null
  const { tenantId, bankName } = target

  /**
   * @description Closes every dialog.
   */
  const closeAll = () => onTarget(null)

  /**
   * @description Returns from an action dialog to the client's details.
   */
  const backToDetails = () => onTarget({ ...target, view: 'details' })

  /**
   * @description Closes everything, reloads the grid and says the client is gone.
   */
  const gone = () => {
    onTarget(null)
    refreshSales()
    onFlash(t('sales.activeClients.flash.gone', { bank: bankName }))
  }

  /**
   * @description Closes everything and shows a confirmation.
   * @param {string} key - The message's key under `sales.activeClients.flash`.
   * @param {string} bank - The bank's name.
   */
  const done = (key: string, bank: string) => {
    onTarget(null)
    onFlash(t(`sales.activeClients.flash.${key}`, { bank }))
  }

  /**
   * @description Opens one of the details modal's action dialogs.
   * @param {ClientDialogKind} view - The dialog to open.
   */
  const openAction = (view: ClientDialogKind) => onTarget({ ...target, view })

  /**
   * @description Shows the confirmation for a resent invitation; the details modal stays open.
   * @param {string} bank - The bank's name.
   */
  const resent = (bank: string) => onFlash(t('sales.activeClients.flash.resent', { bank }))

  /**
   * @description Closes everything and confirms the HR Admin was invited.
   * @param {string} bank - The bank's name.
   */
  const invited = (bank: string) => done('invited', bank)

  /**
   * @description Closes everything and confirms the client was edited.
   * @param {string} bank - The bank's name.
   */
  const edited = (bank: string) => done('edited', bank)

  /**
   * @description Closes everything and confirms the client was archived or restored.
   * @param {ClientArchiveMode} mode - Which change was made.
   * @param {string} bank - The bank's name.
   */
  const archived = (mode: ClientArchiveMode, bank: string) =>
    done(mode === 'archive' ? 'archived' : 'restored', bank)

  switch (target.view) {
    case 'invite':
      return (
        <InviteHrAdminDialog
          tenantId={tenantId}
          bankName={bankName}
          onClose={backToDetails}
          onInvited={invited}
        />
      )
    case 'edit':
      return (
        <EditClientDialog
          tenantId={tenantId}
          bankName={bankName}
          onClose={backToDetails}
          onEdited={edited}
          onGone={gone}
        />
      )
    case 'archive':
    case 'restore':
      return (
        <ClientArchiveDialog
          mode={target.view}
          tenantId={tenantId}
          bankName={bankName}
          onClose={backToDetails}
          onDone={archived}
        />
      )
    default:
      return (
        <ClientDetailModal
          tenantId={tenantId}
          bankName={bankName}
          onClose={closeAll}
          onAction={openAction}
          onResent={resent}
          onGone={gone}
        />
      )
  }
}

export default ActiveClientsDialogs
